import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Typography,
  Collapse,
  useTheme,
  useMediaQuery,
  Chip,
} from '@mui/material';
import { colors } from '../styles/theme';
import SupportDialog from '../components/SupportDialog';
import {
  Dashboard as DashboardIcon,
  CalendarMonth as CalendarIcon,
  Person as PersonIcon,
  LocalHospital as HospitalIcon,
  MedicalServices as MedicalIcon,
  Assignment as AssignmentIcon,
  AttachMoney as MoneyIcon,
  Assessment as AssessmentIcon,
  Settings as SettingsIcon,
  People as PeopleIcon,
  MeetingRoom as RoomIcon,
  Inventory as InventoryIcon,
  Receipt as ReceiptIcon,
  ExpandLess,
  ExpandMore,
  History as HistoryIcon,
  Favorite as FavoriteIcon,
  VideoCall as VideoCallIcon,
  Message as MessageIcon,
  Notifications as NotificationsIcon,
  Help as HelpIcon,
  AdminPanelSettings as AdminIcon,
  Schedule as ScheduleIcon,
  Payment as PaymentIcon,
  Report as ReportIcon,
  Business as BusinessIcon,
  NotificationImportant as ReminderIcon,
  Storage as StorageIcon,
} from '@mui/icons-material';

interface SidebarProps {
  open: boolean;
  mobileOpen: boolean;
  onClose: () => void;
  width: number;
  variant?: 'default' | 'dashboard' | 'minimal';
  userRole?: 'admin' | 'doctor' | 'assistant';
}

interface MenuItem {
  title: string;
  path: string;
  icon: React.ReactElement;
  badge?: string | number;
  children?: MenuItem[];
  roles?: string[];
}

const Sidebar: React.FC<SidebarProps> = ({
  open,
  mobileOpen,
  onClose,
  width,
  variant = 'default',
  userRole = 'assistant',
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [expandedItems, setExpandedItems] = React.useState<string[]>([]);
  const [supportDialogOpen, setSupportDialogOpen] = useState(false);

  const handleExpandClick = (title: string) => {
    setExpandedItems((prev) =>
      prev.includes(title)
        ? prev.filter((item) => item !== title)
        : [...prev, title]
    );
  };

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Menu items based on user role
  const getMenuItems = (): MenuItem[] => {
    const commonItems: MenuItem[] = [
      {
        title: 'Anasayfa',
        path: `/${userRole}`,
        icon: <DashboardIcon />,
      },
    ];

    switch (userRole) {
      case 'admin':
        return [
          ...commonItems,
          {
            title: 'Yönetim',
            path: '/users',
            icon: <AdminIcon />,
            children: [
              {
                title: 'Kullanıcılar',
                path: '/users',
                icon: <PeopleIcon />,
              },
              {
                title: 'Doktorlar',
                path: '/users/doctors',
                icon: <MedicalIcon />,
              },
              {
                title: 'Hastalar',
                path: '/users/patients',
                icon: <PersonIcon />,
              },
              {
                title: 'Personel',
                path: '/users',
                icon: <PeopleIcon />,
              },
            ],
          },
          {
            title: 'Hastane Yönetimi',
            path: '/admin/hospital',
            icon: <HospitalIcon />,
            children: [
              {
                title: 'Departmanlar',
                path: '/admin/departments',
                icon: <BusinessIcon />,
              },
              {
                title: 'Odalar',
                path: '/admin/rooms',
                icon: <RoomIcon />,
              },
              {
                title: 'Envanter',
                path: '/admin/inventory',
                icon: <InventoryIcon />,
              },
            ],
          },
          {
            title: 'Randevular',
            path: '/admin/appointments',
            icon: <CalendarIcon />,
          },
          {
            title: 'Finans',
            path: '/admin/finance',
            icon: <MoneyIcon />,
            children: [
              {
                title: 'Faturalar',
                path: '/admin/invoices',
                icon: <ReceiptIcon />,
              },
              {
                title: 'Ödemeler',
                path: '/admin/payments',
                icon: <PaymentIcon />,
              },
              {
                title: 'Raporlar',
                path: '/admin/reports',
                icon: <ReportIcon />,
              },
            ],
          },
          {
            title: 'Veritabanı Yönetimi',
            path: '/admin/database',
            icon: <StorageIcon />,
            badge: 'NEW',
          },
          {
            title: 'Raporlar',
            path: '/admin/analytics',
            icon: <AssessmentIcon />,
          },
          {
            title: 'Ayarlar',
            path: '/settings',
            icon: <SettingsIcon />,
          },
        ];

      case 'doctor':
        return [
          ...commonItems,
          {
            title: 'Randevularım',
            path: '/appointments',
            icon: <CalendarIcon />,
          },
          {
            title: 'Hastalarım',
            path: '/users/patients',
            icon: <PeopleIcon />,
          },
          {
            title: 'Takvim',
            path: '/appointments/calendar',
            icon: <ScheduleIcon />,
          },
          {
            title: 'Ayarlar',
            path: '/settings',
            icon: <SettingsIcon />,
          },
        ];

      case 'assistant':
        return [
          ...commonItems,
          {
            title: 'Randevu Yönetimi',
            path: '/assistant/appointments',
            icon: <CalendarIcon />,
          },
          {
            title: 'Hasta Kayıt',
            path: '/users/patients',
            icon: <PersonIcon />,
          },
          {
            title: 'Doktor Takvimleri',
            path: '/assistant/doctor-calendars',
            icon: <ScheduleIcon />,
          },
          {
            title: 'Oda Yönetimi',
            path: '/assistant/rooms',
            icon: <RoomIcon />,
          },
          {
            title: 'Anımsatıcılar',
            path: '/assistant/reminders',
            icon: <ReminderIcon />,
          },
          {
            title: 'Ayarlar',
            path: '/settings',
            icon: <SettingsIcon />,
          },
        ];

      default:
        return commonItems;
    }
  };

  const menuItems = getMenuItems();

  const renderMenuItem = (item: MenuItem, depth = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.includes(item.title);
    const active = isActive(item.path);

    return (
      <React.Fragment key={item.title}>
        <ListItem disablePadding sx={{ display: 'block' }}>
          <ListItemButton
            sx={{
              minHeight: 48,
              justifyContent: open || !isMobile ? 'initial' : 'center',
              px: 2.5,
              pl: depth > 0 ? 4 : 2.5,
              backgroundColor: active ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
              },
            }}
            onClick={() => {
              if (hasChildren) {
                handleExpandClick(item.title);
              } else {
                navigate(item.path);
                if (isMobile) {
                  onClose();
                }
              }
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: (open || !isMobile) ? 3 : 'auto',
                justifyContent: 'center',
                color: active ? colors.white : 'rgba(255, 255, 255, 0.7)',
              }}
            >
              {item.icon}
            </ListItemIcon>
            {(open || !isMobile) && (
              <>
                <ListItemText
                  primary={item.title}
                  primaryTypographyProps={{
                    fontSize: depth > 0 ? '0.875rem' : '1rem',
                    fontWeight: active ? 600 : 400,
                    color: active ? colors.white : 'rgba(255, 255, 255, 0.7)',
                    fontFamily: '"SF Pro Text", -apple-system, BlinkMacSystemFont, sans-serif',
                  }}
                />
                {item.badge && (
                  <Chip
                    label={item.badge}
                    size="small"
                    color={typeof item.badge === 'string' ? 'default' : 'error'}
                    sx={{ ml: 1 }}
                  />
                )}
                {hasChildren && (isExpanded ? <ExpandLess /> : <ExpandMore />)}
              </>
            )}
          </ListItemButton>
        </ListItem>
        {hasChildren && (open || !isMobile) && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children!.map((child) => renderMenuItem(child, depth + 1))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  const drawerContent = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: colors.navy,
        fontFamily: '"SF Pro Text", -apple-system, BlinkMacSystemFont, sans-serif',
      }}
    >
      {/* Logo Area - Only for mobile or when header doesn't show it */}
      {(isMobile || variant === 'minimal') && (
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
          <HospitalIcon sx={{ mr: 1, color: colors.white }} />
          <Typography
            variant="h6"
            noWrap
            sx={{
              fontFamily: '"SF Pro Text", -apple-system, BlinkMacSystemFont, sans-serif',
              color: colors.white,
            }}
          >
            DrSync
          </Typography>
        </Box>
      )}

      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />

      {/* Menu Items */}
      <List sx={{ flex: 1, overflowY: 'auto' }}>
        {menuItems.map((item) => renderMenuItem(item))}
      </List>

      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />

      {/* Help Section */}
      <List>
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => {
              setSupportDialogOpen(true);
              if (isMobile) {
                onClose();
              }
            }}
            sx={{
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
              },
            }}
          >
            <ListItemIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              <HelpIcon />
            </ListItemIcon>
            {(open || !isMobile) && (
              <ListItemText
                primary="Yardım & Destek"
                primaryTypographyProps={{
                  fontFamily: '"SF Pro Text", -apple-system, BlinkMacSystemFont, sans-serif',
                  color: 'rgba(255, 255, 255, 0.7)',
                }}
              />
            )}
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  const handleCloseSupportDialog = () => {
    setSupportDialogOpen(false);
  };

  return (
    <>
      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onClose}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width,
            backgroundColor: colors.navy,
            borderRadius: 0,
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop Drawer */}
      <Drawer
        variant="permanent"
        open={open}
        sx={{
          display: { xs: 'none', md: 'block' },
          width: 0, // Always 0 so it doesn't take flex space
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            position: 'fixed',
            width: open ? width : 0,
            boxSizing: 'border-box',
            top: '64px', // Height of the AppBar
            height: 'calc(100% - 64px)',
            backgroundColor: colors.navy,
            borderRight: open ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
            borderRadius: 0,
            overflow: 'hidden',
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Support Dialog */}
      <SupportDialog
        open={supportDialogOpen}
        onClose={handleCloseSupportDialog}
      />
    </>
  );
};

export default Sidebar;